import { useCallback, useEffect, useState } from 'react';
import { Layer, useMapInstance } from 'shared/map-exports';
import { PrivateLandOwnershipEntity } from 'shared/types';
import { useMapDrawerEntityForPoiState } from 'state/useMapDrawerEntityForPoiState';
import {
  getSelectedPatternStyle,
  getSelectedStrokeStyle,
} from 'components/Map/utils';
import { Map, MapSourceDataEvent } from 'mapbox-gl';
import { debounce } from 'lodash-es';
import {
  layerBorderStyle,
  layerFillStyle,
  layerLabelStyle,
} from './layerStyles';
import VisibilityToggledSource from '../VisibilityToggledSource';
import { getWatchdutyStaticTileUrl } from '../getWatchdutyStaticTileUrl';
import { useMapLayerEvents } from '../useMapLayerEvents';
import {
  FILL_LAYER_ID,
  SOURCE_ID,
  SOURCE_DATA_POLYGONS_LAYER_ID,
  LABEL_LAYER_ID,
} from './constants';

type PrivateLandOwnershipLayerProps = {
  visible: boolean;
};

type FeatureProperties = {
  owner?: string;
  address: string;
  mailadd?: string;
  struct: boolean;
  usps_vacancy_date: string;
  zoning_type: string;
  zoning_subtype: string;
  lat: string;
  lon: string;
  fema_flood_zone: string;
  fema_flood_zone_data_date: string;
  fema_flood_zone_subtype: string;
  ll_bldg_count: number;
  ll_gisacre: number;
  ll_last_refresh: string;
  ll_updated_at: string;
};

// some addresses display "NONE" for the street number
const removeNone = (address: string): string =>
  address.replace(/none/i, '').replace(/ {2}/i, '');

const debouncedShowLabels = debounce(
  (map: Map) => {
    if (!map.isStyleLoaded() || !map.getLayer(LABEL_LAYER_ID)) return;
    map.setLayoutProperty(LABEL_LAYER_ID, 'visibility', 'visible');
  },
  350,
  { trailing: true },
);

export const PrivateLandOwnershipLayer = (
  props: PrivateLandOwnershipLayerProps,
): JSX.Element => {
  const { visible } = props;
  const { mapDrawerEntityForPoi, setMapDrawerEntityForPoi } =
    useMapDrawerEntityForPoiState();
  const mapRef = useMapInstance();
  const [featuresNum, setFeaturesNum] = useState<number>(0);

  /**
   * This useEffect tries to reduce webview crashes on some mobile devices related to
   * "Too many glyphs being rendered in a tile" by hiding labels while a user
   * is zooming in or out.
   */
  useEffect(() => {
    const map = mapRef?.getMap();

    if (!map) {
      return () => {};
    }

    const hideLabels = (): void => {
      if (!map.isStyleLoaded() || !map.getLayer(LABEL_LAYER_ID)) return;
      map.setLayoutProperty(LABEL_LAYER_ID, 'visibility', 'none');
    };

    map.on('zoomstart', hideLabels);
    map.on('movestart', hideLabels);
    map.on('zoomend', () => debouncedShowLabels(map));
    map.on('moveend', () => debouncedShowLabels(map));
    map.on('style.load', () => debouncedShowLabels(map));

    return () => {
      map.off('zoomstart', hideLabels);
      map.off('movestart', hideLabels);
      map.off('zoomend', () => debouncedShowLabels(map));
      map.off('moveend', () => debouncedShowLabels(map));
      map.off('style.load', () => debouncedShowLabels(map));
    };
  }, [mapRef]);

  useEffect(() => {
    const map = mapRef?.getMap();

    if (!map || !map.isStyleLoaded() || !map.getLayer(LABEL_LAYER_ID)) return;

    if (!featuresNum) {
      map.setLayoutProperty(LABEL_LAYER_ID, 'visibility', 'none');
      return;
    }

    // Empirical number chosen based on testing and experimentation.
    const DENSITY_THRESHOLD = 15000;

    if (featuresNum > DENSITY_THRESHOLD) {
      /**
       * This filter hides labels with owner names longer than 18 characters at zoom levels
       * of 16-18, and at zoom levels of 13-15 hides names longer than 12 characters. This should
       * help prevent the "Too many glyphs being rendered in a tile" warning that ends up crashing
       * the webview on some mobile devices.
       */
      map.setFilter(LABEL_LAYER_ID, [
        'all',
        ['has', 'owner'],
        [
          'case',
          ['>', ['zoom'], 15],
          ['<', ['length', ['to-string', ['get', 'owner']]], 18], // Zoom 16-18
          ['<', ['length', ['to-string', ['get', 'owner']]], 12], // Zoom 13-15
        ],
      ]);
    } else {
      map.setFilter(LABEL_LAYER_ID, ['has', 'owner']);
    }

    debouncedShowLabels(map);
  }, [featuresNum, mapRef]);

  useEffect(() => {
    const map = mapRef?.getMap();

    if (!map) {
      return () => {};
    }

    const getSourceFeatures = (e: MapSourceDataEvent): void => {
      if (e.sourceId !== SOURCE_ID || !e.isSourceLoaded) {
        return;
      }
      const sourceFeatures = map.querySourceFeatures(SOURCE_ID, {
        sourceLayer: SOURCE_DATA_POLYGONS_LAYER_ID,
      });

      setFeaturesNum(sourceFeatures.length);
    };

    map.on('sourcedata', getSourceFeatures);

    return () => {
      map.off('sourcedata', getSourceFeatures);
    };
  }, [mapRef, setFeaturesNum]);

  useMapLayerEvents<FeatureProperties>({
    layerId: FILL_LAYER_ID,
    onClick: useCallback(
      (geojsonFeatures) => {
        const feature = geojsonFeatures[0];

        const metadata: PrivateLandOwnershipEntity = {
          id: feature.id as number,
          owner: feature.properties.owner || '-',
          address: removeNone(feature.properties.address || '-'),
          taxAddress: removeNone(feature.properties.mailadd || '-'),
          lat: feature.properties.lat,
          lng: feature.properties.lon,
          acres: feature.properties.ll_gisacre,
          buildings: feature.properties.ll_bldg_count,
          zoningType: feature.properties.zoning_type,
          zoningSubtype: feature.properties.zoning_subtype,
          femaFloodZone: feature.properties.fema_flood_zone,
          femaFloodZoneSubtype: feature.properties.fema_flood_zone_subtype,
          femaFloodZoneDate: feature.properties.fema_flood_zone_data_date,
        };

        setMapDrawerEntityForPoi({
          id: feature.id as number,
          type: 'privateLandOwnership',
          metadata,
        });
      },
      [setMapDrawerEntityForPoi],
    ),
  });

  const selectedId =
    (mapDrawerEntityForPoi?.type === 'privateLandOwnership' &&
      mapDrawerEntityForPoi?.id) ||
    '';

  const selectedFillStyle = getSelectedPatternStyle({
    idKey: ['id'],
    otherLayerProps: { 'source-layer': SOURCE_DATA_POLYGONS_LAYER_ID },
    selectedId,
  });

  const selectedStrokeStyle = getSelectedStrokeStyle({
    idKey: ['id'],
    otherLayerProps: { 'source-layer': SOURCE_DATA_POLYGONS_LAYER_ID },
    selectedId,
  });

  return (
    <VisibilityToggledSource
      url={getWatchdutyStaticTileUrl('land_ownership_private')}
      visible={visible}
      id={SOURCE_ID}
    >
      <Layer {...layerFillStyle} />
      <Layer {...layerBorderStyle} />
      <Layer {...layerLabelStyle} />
      <Layer {...selectedFillStyle} />
      <Layer {...selectedStrokeStyle} />
    </VisibilityToggledSource>
  );
};
